import { NormalizedExpression } from '../NormalizedExpression/types';
import * as diff from './diff';

export type ChangeStatus = 'unchanged' | 'added' | 'removed' | 'modified';

export type DeltaValue = {
  status: ChangeStatus;
  before?: NormalizedExpression.Value;
  after?: NormalizedExpression.Value;
};

export type DeltaOperator<T> = {
  status: ChangeStatus;
  before?: T;
  after?: T;
};

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace NormalizedExpressionDelta {
  export type BinaryExpressionDelta = {
    type: 'binary';
    status: ChangeStatus;
    attributeName: string;
    operator: DeltaOperator<NormalizedExpression.BinaryOperator>;
    value: DeltaValue;
  };

  export type UnaryExpressionDelta = {
    type: 'unary';
    status: ChangeStatus;
    attributeName: string;
    operator: DeltaOperator<NormalizedExpression.UnaryOperator>;
  };

  export type RangeExpressionDelta = {
    type: 'range';
    status: ChangeStatus;
    attributeName: string;
    operator: DeltaOperator<NormalizedExpression.RangeOperator>;
    range: {
      start: DeltaValue;
      end: DeltaValue;
      startInclusive: DeltaOperator<boolean>;
      endInclusive: DeltaOperator<boolean>;
    };
  };

  export type SetExpressionDelta = {
    type: 'set';
    status: ChangeStatus;
    attributeName: string;
    operator: DeltaOperator<NormalizedExpression.SetOperator>;
    values: DeltaValue[];
  };

  export type SegmentExpressionDelta = {
    type: 'segment';
    status: ChangeStatus;
    operator: DeltaOperator<NormalizedExpression.SegmentOperator>;
    segmentId: DeltaValue;
  };

  export type LogicalExpressionDelta = {
    type: 'logical';
    status: ChangeStatus;
    operator: DeltaOperator<'and' | 'or'>;
    operands: ExpressionDelta[];
  };

  export type InnerExpressionDelta =
    | Omit<BinaryExpressionDelta, 'attributeName' | 'status'>
    | Omit<RangeExpressionDelta, 'attributeName' | 'status'>
    | Omit<SetExpressionDelta, 'attributeName' | 'status'>;

  export type ArrayExpressionDelta = {
    type: 'array';
    status: ChangeStatus;
    attributeName: string;
    operator: DeltaOperator<NormalizedExpression.ArrayOperator>;
    rule: InnerExpressionDelta;
  };

  export type ExpressionDelta =
    | BinaryExpressionDelta
    | UnaryExpressionDelta
    | RangeExpressionDelta
    | SetExpressionDelta
    | SegmentExpressionDelta
    | LogicalExpressionDelta
    | ArrayExpressionDelta;

  export const valuesEqual = (
    a: NormalizedExpression.Value,
    b: NormalizedExpression.Value,
  ): boolean => {
    if (a === b) {
      return true;
    }
    if (a === null || b === null) {
      return false;
    }
    if (typeof a === 'object' && typeof b === 'object') {
      if ('version' in a && 'version' in b) {
        return a.version === b.version;
      }
    }
    return false;
  };

  export const createUnchangedValue = (
    value: NormalizedExpression.Value,
  ): DeltaValue => ({
    status: 'unchanged',
    before: value,
    after: value,
  });

  export const createAddedValue = (
    value: NormalizedExpression.Value,
  ): DeltaValue => ({
    status: 'added',
    after: value,
  });

  export const createRemovedValue = (
    value: NormalizedExpression.Value,
  ): DeltaValue => ({
    status: 'removed',
    before: value,
  });

  export const createModifiedValue = (
    before: NormalizedExpression.Value,
    after: NormalizedExpression.Value,
  ): DeltaValue => ({
    status: 'modified',
    before,
    after,
  });

  export const createUnchangedOperator = <T>(
    operator: T,
  ): DeltaOperator<T> => ({
    status: 'unchanged',
    before: operator,
    after: operator,
  });

  export const createAddedOperator = <T>(operator: T): DeltaOperator<T> => ({
    status: 'added',
    after: operator,
  });

  export const createRemovedOperator = <T>(operator: T): DeltaOperator<T> => ({
    status: 'removed',
    before: operator,
  });

  export const createModifiedOperator = <T>(
    before: T,
    after: T,
  ): DeltaOperator<T> => ({
    status: 'modified',
    before,
    after,
  });

  export const diffNormalizedExpression = diff.diffNormalizedExpression;

  export const fromNormalizedExpression = (
    expr: NormalizedExpression.Expression,
  ): ExpressionDelta => {
    switch (expr.type) {
      case 'binary': {
        return {
          type: 'binary',
          status: 'unchanged',
          attributeName: expr.attributeName,
          operator: createUnchangedOperator(expr.operator),
          value: createUnchangedValue(expr.value),
        };
      }
      case 'unary': {
        return {
          type: 'unary',
          status: 'unchanged',
          attributeName: expr.attributeName,
          operator: createUnchangedOperator(expr.operator),
        };
      }
      case 'range': {
        return {
          type: 'range',
          status: 'unchanged',
          attributeName: expr.attributeName,
          operator: createUnchangedOperator(expr.operator),
          range: {
            start: createUnchangedValue(expr.range.start),
            end: createUnchangedValue(expr.range.end),
            startInclusive: createUnchangedOperator(expr.range.startInclusive),
            endInclusive: createUnchangedOperator(expr.range.endInclusive),
          },
        };
      }
      case 'set': {
        return {
          type: 'set',
          status: 'unchanged',
          attributeName: expr.attributeName,
          operator: createUnchangedOperator(expr.operator),
          values: expr.values.map(v => createUnchangedValue(v)),
        };
      }
      case 'segment': {
        return {
          type: 'segment',
          status: 'unchanged',
          operator: createUnchangedOperator(expr.operator),
          segmentId: createUnchangedValue(expr.segmentId),
        };
      }
      case 'logical': {
        return {
          type: 'logical',
          status: 'unchanged',
          operator: createUnchangedOperator(expr.operator),
          operands: expr.operands.map(fromNormalizedExpression),
        };
      }
      case 'array': {
        const innerRule = expr.rule;
        let ruleDelta: InnerExpressionDelta;
        if (innerRule.type === 'binary') {
          ruleDelta = {
            type: 'binary',
            operator: createUnchangedOperator(innerRule.operator),
            value: createUnchangedValue(innerRule.value),
          };
        } else if (innerRule.type === 'range') {
          ruleDelta = {
            type: 'range',
            operator: createUnchangedOperator(innerRule.operator),
            range: {
              start: createUnchangedValue(innerRule.range.start),
              end: createUnchangedValue(innerRule.range.end),
              startInclusive: createUnchangedOperator(
                innerRule.range.startInclusive,
              ),
              endInclusive: createUnchangedOperator(
                innerRule.range.endInclusive,
              ),
            },
          };
        } else {
          // innerRule.type === 'set'
          ruleDelta = {
            type: 'set',
            operator: createUnchangedOperator(innerRule.operator),
            values: innerRule.values.map(v => createUnchangedValue(v)),
          };
        }
        return {
          type: 'array',
          status: 'unchanged',
          attributeName: expr.attributeName,
          operator: createUnchangedOperator(expr.operator),
          rule: ruleDelta,
        };
      }
      default: {
        throw new Error('Unknown expression type');
      }
    }
  };
}
